/**
 * Recover a Google/Facebook click id when it is not on the current URL.
 *
 * Ads for maayot land on `maayot.com/landing-page/...`, and those pages link on
 * to `story.maayot.com/signup` as a bare URL — no query string. So by the time a
 * visitor reaches the app, `?gclid=` is long gone, and the backend's offline
 * conversion upload has nothing to match a purchase against. That is why Google
 * Ads reported no conversions even once the credentials were in place.
 *
 * The click id is still reachable, though. Google Ads auto-tagging plus the
 * Conversion Linker tag in GTM-5X8ZHGK write a `_gcl_aw` cookie on the highest
 * resolvable domain — `.maayot.com` — which is readable from `story.maayot.com`
 * because they share a root domain. Reading the cookie recovers the click id
 * without touching the landing pages, and it keeps working however the visitor
 * wanders between the two sites before signing up.
 *
 * Cookie format is `GCL.<timestampSeconds>.<gclid>`; the click id itself may
 * contain dots, so only the first two segments are stripped. Facebook's `_fbc`
 * follows `fb.<subdomainIndex>.<timestampMs>.<fbclid>`.
 */

/** Read a cookie by name. Returns null when absent or unreadable. */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'),
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    // Cookies can throw in sandboxed frames and hardened privacy modes.
    return null;
  }
}

/**
 * The Google click id from the `_gcl_aw` cookie, or null.
 * `GCL.1725340000.Cj0KCQ...` -> `Cj0KCQ...`
 */
export function gclidFromCookie(): string | null {
  const raw = readCookie('_gcl_aw');
  if (!raw) return null;

  const parts = raw.split('.');
  // Anything shorter than GCL + timestamp + id is not a click id we can use.
  if (parts.length < 3) return null;

  const gclid = parts.slice(2).join('.');
  return gclid || null;
}

/** The full `_fbc` cookie (`fb.1.<clickTimeMs>.<fbclid>`), or null. */
export function fbcFromCookie(): string | null {
  return readCookie('_fbc');
}

/** The `_fbp` browser id cookie the Meta pixel sets, or null. */
export function fbpFromCookie(): string | null {
  return readCookie('_fbp');
}

/**
 * The Facebook click id from the `_fbc` cookie, or null.
 * `fb.1.1725340000000.IwAR...` -> `IwAR...`
 */
export function fbclidFromCookie(): string | null {
  const raw = readCookie('_fbc');
  if (!raw) return null;

  const parts = raw.split('.');
  if (parts.length < 4) return null;

  const fbclid = parts.slice(3).join('.');
  return fbclid || null;
}
